import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Center,
  Spinner,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  useToast,
  AspectRatio
} from '@chakra-ui/react';
import { FaDownload, FaExpand, FaCompress, FaExternalLinkAlt } from 'react-icons/fa';
import { HiOutlineDocumentText } from 'react-icons/hi';
import axios from 'axios';
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const DocumentPreview = ({ isOpen, onClose, document, clienteId }) => {
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const toast = useToast();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    if (isOpen && document) {
      fetchPreviewUrl();
    }
  }, [isOpen, document]);

  const fetchPreviewUrl = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/${document._id}/preview`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPreviewUrl(response.data.data.url);
    } catch (error) {
      console.error('Error fetching preview:', error);
      setError('Erro ao carregar preview do documento');
      toast({
        title: 'Erro ao carregar preview',
        description: error.response?.data?.message || 'Erro ao carregar preview do documento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/clientes/${clienteId}/documents/${document._id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: 'Erro ao baixar documento',
        description: 'Erro ao baixar documento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <Center h="500px">
          <VStack>
            <Spinner size="xl" color="green.500" />
            <Text color={textColor}>Carregando preview...</Text>
          </VStack>
        </Center>
      );
    }

    if (error) {
      return (
        <Center h="500px">
          <VStack spacing={4}>
            <Icon as={HiOutlineDocumentText} boxSize={20} color="gray.400" />
            <Text color={textColor}>{error}</Text>
            <Button
              leftIcon={<FaDownload />}
              colorScheme="green"
              onClick={handleDownload}
            >
              Baixar Documento
            </Button>
          </VStack>
        </Center>
      );
    }

    // For PDF and text files, use iframe
    if (document.documentType === 'pdf' || document.documentType === 'txt') {
      return (
        <Box h={isFullscreen ? '80vh' : '500px'} w="100%">
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title={document.originalName}
          />
        </Box>
      );
    }

    // For Word documents and others, show download option
    return (
      <Center h="400px" bg={bgColor} borderRadius="md">
        <VStack spacing={4}>
          <Icon as={HiOutlineDocumentText} boxSize={20} color="gray.400" />
          <Text color={textColor} textAlign="center">
            Preview não disponível para arquivos {document.documentType.toUpperCase()}
          </Text>
          <Text fontSize="sm" color={textColor}>
            Clique abaixo para baixar e visualizar o documento
          </Text>
          <Button
            leftIcon={<FaDownload />}
            colorScheme="green"
            onClick={handleDownload}
          >
            Baixar Documento
          </Button>
        </VStack>
      </Center>
    );
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size={isFullscreen ? 'full' : '6xl'}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack justify="space-between">
            <Text>{document?.originalName}</Text>
            <HStack>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={isFullscreen ? <FaCompress /> : <FaExpand />}
                onClick={() => setIsFullscreen(!isFullscreen)}
              >
                {isFullscreen ? 'Reduzir' : 'Expandir'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaExternalLinkAlt />}
                onClick={() => window.open(previewUrl, '_blank')}
              >
                Abrir em nova aba
              </Button>
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={4}>
          {renderPreview()}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Fechar
          </Button>
          <Button colorScheme="green" leftIcon={<FaDownload />} onClick={handleDownload}>
            Baixar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DocumentPreview;
